/**
 * Check Recent Test Messages
 * Queries recent messages to see what was sent and delivery status
 */

import { db } from '../firebase';
import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';

export async function checkRecentTestMessages() {
  console.log('🔍 Checking recent test messages...');

  try {
    // Get messages from last 30 minutes
    const thirtyMinutesAgo = Timestamp.fromDate(new Date(Date.now() - 30 * 60 * 1000));

    // Query flat messages collection (pre-migration)
    const messagesQuery = query(
      collection(db, 'messages'),
      where('createdAt', '>=', thirtyMinutesAgo),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const snapshot = await getDocs(messagesQuery);

    console.log(`\n📊 Found ${snapshot.size} messages in last 30 minutes`);

    if (snapshot.empty) {
      console.log('✅ No recent messages found - nothing was sent');
      return;
    }

    // Group by metadata category
    const testMessages = [];
    const regularMessages = [];

    snapshot.forEach(doc => {
      const data = doc.data();
      const message = {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || data.createdAt
      };

      if (data.metadata?.category === 'system_test') {
        testMessages.push(message);
      } else {
        regularMessages.push(message);
      }
    });

    console.log(`\n🧪 Test Messages: ${testMessages.length}`);
    console.log(`📬 Regular Messages: ${regularMessages.length}`);

    if (testMessages.length > 0) {
      console.log('\n=== TEST MESSAGE DETAILS ===\n');

      // Group by target audience
      const toSelf = testMessages.filter(m => m.metadata?.targetAudience === 'self_only');
      const toAll = testMessages.filter(m => m.metadata?.targetAudience === 'all_parents');

      console.log(`📤 "Send to Me Only": ${toSelf.length} messages`);
      console.log(`📣 "Send to All Parents": ${toAll.length} messages`);

      if (toAll.length > 0) {
        console.log('\n⚠️  ALERT: Messages sent to ALL parents:');
        console.log('─────────────────────────────────────────');

        // Get unique parent IDs
        const uniqueParents = new Set(toAll.map(m => m.parentId));
        console.log(`\n👥 Unique parents: ${uniqueParents.size}`);

        // Check delivery status
        toAll.forEach((msg, idx) => {
          console.log(`\n[${idx + 1}] Message ID: ${msg.id}`);
          console.log(`   Parent: ${msg.parentId.substring(0, 8)}...`);
          console.log(`   Student: ${msg.studentId}`);
          console.log(`   Status: ${msg.status}`);
          console.log(`   Subject: ${msg.content?.subject || 'No subject'}`);
          console.log(`   Created: ${msg.createdAt?.toLocaleString?.() || msg.createdAt}`);

          // Check delivery channels
          if (msg.delivery) {
            console.log(`   Delivery:`);
            Object.entries(msg.delivery).forEach(([channel, status]) => {
              if (status.enabled) {
                const delivered = status.sentAt ? '✅ SENT' : '⏳ PENDING';
                const sentTime = status.sentAt?.toDate?.()?.toLocaleTimeString?.() || 'Not sent yet';
                console.log(`     ${channel}: ${delivered} ${status.sentAt ? `at ${sentTime}` : ''}`);
              }
            });
          }
        });

        // Show message content sample
        if (toAll.length > 0) {
          console.log('\n📝 MESSAGE CONTENT (first message):');
          console.log('─────────────────────────────────────────');
          console.log(toAll[0].content?.body || 'No body');
          console.log('─────────────────────────────────────────');
        }
      }

      if (toSelf.length > 0) {
        console.log('\n✅ Messages sent to self only:');
        console.log(`   Count: ${toSelf.length}`);
        console.log(`   Status: ${toSelf[0].status}`);
      }
    }

    // Return summary
    return {
      total: snapshot.size,
      testMessages: testMessages.length,
      toSelf: testMessages.filter(m => m.metadata?.targetAudience === 'self_only').length,
      toAll: testMessages.filter(m => m.metadata?.targetAudience === 'all_parents').length,
      messages: testMessages
    };

  } catch (error) {
    console.error('❌ Error checking test messages:', error);
    throw error;
  }
}

// Make available globally
if (typeof window !== 'undefined') {
  window.checkRecentTestMessages = checkRecentTestMessages;
}
